import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Building2, Link, Save, X, AlertCircle, Check, Loader2, Upload } from 'lucide-react'
import { z } from 'zod'
import { useUpdateCompany } from '../../hooks/useUpdateCompany'
import type { Company } from '../../hooks/useCompanies'

// Zod schema for form validation
export const editCompanySchema = z.object({
  name: z.string().min(2, 'Company name must be at least 2 characters').max(255, 'Company name must be less than 255 characters'),
  description: z.string().max(2000, 'Description must be less than 2000 characters').optional(),
  website: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  logoUrl: z.string().url('Must be a valid URL').optional().or(z.literal(''))
})

export type EditCompanyFormData = z.infer<typeof editCompanySchema>

interface EditCompanyFormProps {
  company: Company
  onSuccess?: () => void
  onCancel?: () => void
}

export function EditCompanyForm({
  company,
  onSuccess,
  onCancel
}: EditCompanyFormProps) {
  const [formData, setFormData] = useState<EditCompanyFormData>({
    name: company.name,
    description: company.description || '',
    website: company.website || '',
    logoUrl: company.logoUrl || ''
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const { updateCompany, loading, error } = useUpdateCompany()

  const [showSuccess, setShowSuccess] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  // Reset form when company changes
  useEffect(() => {
    setFormData({
      name: company.name,
      description: company.description || '',
      website: company.website || '',
      logoUrl: company.logoUrl || ''
    })
    setErrors({})
  }, [company.id, company.name, company.description, company.website, company.logoUrl])

  const validateField = (field: keyof EditCompanyFormData, value: any) => {
    try {
      const partialSchema = editCompanySchema.pick({ [field]: true as never })
      partialSchema.parse({ [field]: value })
      setErrors(prev => ({ ...prev, [field]: '' }))
      return true
    } catch (error) {
      if (error instanceof z.ZodError) {
        setErrors(prev => ({
          ...prev,
          [field]: error.issues[0]?.message || 'Invalid value'
        }))
      }
      return false
    }
  }

  const handleInputChange = (
    field: keyof EditCompanyFormData,
    value: string
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    validateField(field, value)
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, logoUrl: 'Only image files are allowed' }))
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, logoUrl: 'File size must be less than 5MB' }))
      return
    }

    // For now, just store the file name
    // In production, you'd upload to a server and get a URL back
    setFormData(prev => ({ ...prev, logoUrl: file.name }))
    setErrors(prev => ({ ...prev, logoUrl: '' }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate all fields
    try {
      editCompanySchema.parse(formData)
      setErrors({})
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {}
        error.issues.forEach((err: any) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message
          }
        })
        setErrors(newErrors)
        return
      }
    }

    try {
      // Only send changed fields
      const changes: Partial<EditCompanyFormData> = {}
      if (formData.name !== company.name) changes.name = formData.name
      if (formData.description !== company.description) changes.description = formData.description || undefined
      if (formData.website !== company.website) changes.website = formData.website || undefined
      if (formData.logoUrl !== company.logoUrl) changes.logoUrl = formData.logoUrl || undefined

      // Check if there are any changes
      if (Object.keys(changes).length === 0) {
        setSuccessMessage('No changes to save.')
        setShowSuccess(true)
        setTimeout(() => {
          setShowSuccess(false)
          onSuccess?.()
        }, 1500)
        return
      }

      await updateCompany(company.id, changes)

      setSuccessMessage('Company updated successfully!')
      setShowSuccess(true)

      setTimeout(() => {
        setShowSuccess(false)
        onSuccess?.()
      }, 2000)
    } catch (err: any) {
      // Error is already handled by the hook
      console.error('Failed to update company:', err)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-card rounded-lg border border-border p-6">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Edit Company
          </h2>
          <p className="text-muted-foreground">
            Update company information and settings
          </p>
        </div>

        {/* Success Toast */}
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 p-4 rounded-lg flex items-center gap-3 bg-green-500/10 text-green-600 border border-green-500/20"
            >
              <Check className="w-5 h-5 flex-shrink-0" />
              <p>{successMessage}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error Banner */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 p-4 rounded-lg flex items-center gap-3 bg-red-500/10 text-red-600 border border-red-500/20"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Name (Required) */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Company Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full px-4 py-2 rounded-lg border bg-background ${
                errors.name ? 'border-destructive' : 'border-border'
              } focus:outline-none focus:ring-2 focus:ring-primary`}
              placeholder="Enter company name"
              disabled={loading}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.name}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={4}
              className={`w-full px-4 py-2 rounded-lg border bg-background ${
                errors.description ? 'border-destructive' : 'border-border'
              } focus:outline-none focus:ring-2 focus:ring-primary resize-none`}
              placeholder="Enter a brief description of the company"
              disabled={loading}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.description}
              </p>
            )}
            <p className="mt-1 text-sm text-muted-foreground">
              {formData.description?.length || 0} / 2000 characters
            </p>
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Website
            </label>
            <div className="relative">
              <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="url"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border bg-background ${
                  errors.website ? 'border-destructive' : 'border-border'
                } focus:outline-none focus:ring-2 focus:ring-primary`}
                placeholder="https://example.com"
                disabled={loading}
              />
            </div>
            {errors.website && (
              <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.website}
              </p>
            )}
          </div>

          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Company Logo
            </label>
            <div className="flex items-center gap-3">
              <label className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  disabled={loading}
                />
                <motion.div
                  className="flex items-center justify-center gap-2 px-4 py-2 border border-border rounded-lg cursor-pointer hover:bg-secondary/50 transition-colors"
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                >
                  <Upload className="w-4 h-4" />
                  <span>Choose Image...</span>
                </motion.div>
              </label>
              {formData.logoUrl && (
                <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                  <span className="truncate max-w-xs">{formData.logoUrl}</span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, logoUrl: '' }))}
                    className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                    disabled={loading}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
            {errors.logoUrl && (
              <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.logoUrl}
              </p>
            )}
            <p className="mt-1 text-sm text-muted-foreground">
              Upload a company logo (max 5MB, image files only)
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-border">
            <motion.button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving Changes...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Changes
                </>
              )}
            </motion.button>

            {onCancel && (
              <motion.button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="px-6 py-3 bg-secondary text-secondary-foreground rounded-lg font-semibold hover:bg-secondary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
              >
                Cancel
              </motion.button>
            )}
          </div>
        </form>
      </div>
    </motion.div>
  )
}
